import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom-v7';
/**
 * Reusable tabs layout component with optional routing support.
 * Uses React Router 7.
 *
 * When enableRouting is true, the component supports two routing strategies:
 * - 'path': Uses URL paths (requires 'path' in TabConfig, optional 'basePath' in props)
 * - 'searchParams': Uses query parameters (requires 'searchParamValue' in TabConfig, 'searchParamKey' in props)
 *
 * The component will:
 * - Sync the active tab with the URL
 * - Navigate to the tab's path or update search params when a tab is clicked
 *
 * Note: This component must be rendered within a React Router 7 context.
 */
export default function MFTabs({ tabs, enableRouting = false, routingMode = 'path', basePath, searchParamKey = 'tab', searchParamScopePath, defaultTab, onTabChange, variant = 'nav', ...tabsProps }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Check if search params should be active (only when on the scoped path, if provided)
    const isSearchParamsActive = useMemo(() => {
        if (routingMode !== 'searchParams')
            return false;
        if (!searchParamScopePath)
            return true; // No scope means always active
        return location.pathname === searchParamScopePath || location.pathname.startsWith(`${searchParamScopePath}/`);
    }, [routingMode, searchParamScopePath, location.pathname]);
    // Get current tab from URL when routing is enabled
    const currentTabSlug = useMemo(() => {
        if (!enableRouting || !location)
            return defaultTab || tabs[0]?.slug;
        if (routingMode === 'searchParams') {
            // Only read search params if we're on the scoped path (or no scope is set)
            if (!isSearchParamsActive)
                return defaultTab || tabs[0]?.slug;
            // Check search params using useSearchParams
            const paramValue = searchParams.get(searchParamKey);
            if (paramValue) {
                const tab = tabs.find(t => t.searchParamValue === paramValue);
                if (tab)
                    return tab.slug;
            }
        }
        else {
            // Check path-based routing
            const pathname = location.pathname;
            for (const tab of tabs) {
                if (tab.path) {
                    // Simple path matching (can be enhanced for params)
                    const tabPath = basePath ? `${basePath}/${tab.path}` : tab.path;
                    const tabPathWithSlash = `${tabPath}/`;
                    if (pathname === tabPath || pathname.startsWith(tabPathWithSlash)) {
                        return tab.slug;
                    }
                }
            }
        }
        return defaultTab || tabs[0]?.slug;
    }, [
        enableRouting,
        location,
        tabs,
        basePath,
        defaultTab,
        routingMode,
        searchParamKey,
        searchParams,
        isSearchParamsActive
    ]);
    const currentTabIndex = useMemo(() => {
        const index = tabs.findIndex(tab => tab.slug === currentTabSlug);
        return index >= 0 ? index : 0;
    }, [tabs, currentTabSlug]);
    // Handle tab change
    const handleTabChange = (index) => {
        const tab = tabs[index];
        if (!tab)
            return;
        if (enableRouting) {
            if (routingMode === 'searchParams' && tab.searchParamValue) {
                // Only update search params if we're on the scoped path (or no scope is set)
                if (isSearchParamsActive) {
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set(searchParamKey, tab.searchParamValue);
                    setSearchParams(newSearchParams, { replace: false });
                }
            }
            else if (routingMode === 'path' && tab.path) {
                // Navigate to path
                const targetPath = basePath ? `${basePath}/${tab.path}` : tab.path;
                navigate(targetPath);
            }
        }
        onTabChange?.(tab.slug, index);
    };
    // Cleanup: Remove search params when navigating away from scoped path
    useEffect(() => {
        if (!enableRouting || routingMode !== 'searchParams' || !searchParamScopePath)
            return;
        // If we're not on the scoped path, clean up the search param
        if (!isSearchParamsActive) {
            const currentParamValue = searchParams.get(searchParamKey);
            if (currentParamValue) {
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete(searchParamKey);
                setSearchParams(newSearchParams, { replace: true });
            }
        }
    }, [
        enableRouting,
        routingMode,
        searchParamKey,
        searchParamScopePath,
        searchParams,
        setSearchParams,
        isSearchParamsActive
    ]);
    // Set default tab on mount if routing is enabled and no tab is selected
    useEffect(() => {
        if (!enableRouting)
            return;
        // For search params, only set default if we're on the scoped path
        if (routingMode === 'searchParams' && !isSearchParamsActive)
            return;
        let hasMatchingTab = false;
        if (routingMode === 'searchParams') {
            const paramValue = searchParams.get(searchParamKey);
            hasMatchingTab = !!paramValue && tabs.some(tab => tab.searchParamValue === paramValue);
        }
        else {
            const pathname = location?.pathname || '';
            hasMatchingTab = tabs.some(tab => {
                if (!tab.path)
                    return false;
                const tabPath = basePath ? `${basePath}/${tab.path}` : tab.path;
                const tabPathWithSlash = `${tabPath}/`;
                return pathname === tabPath || pathname.startsWith(tabPathWithSlash);
            });
        }
        if (!hasMatchingTab && defaultTab) {
            const defaultTabConfig = tabs.find(t => t.slug === defaultTab);
            if (!defaultTabConfig)
                return;
            if (routingMode === 'searchParams' && defaultTabConfig.searchParamValue) {
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set(searchParamKey, defaultTabConfig.searchParamValue);
                setSearchParams(newSearchParams, { replace: true });
            }
            else if (routingMode === 'path' && defaultTabConfig.path) {
                const targetPath = basePath ? `${basePath}/${defaultTabConfig.path}` : defaultTabConfig.path;
                navigate(targetPath, { replace: true });
            }
        }
    }, [
        enableRouting,
        location,
        tabs,
        basePath,
        defaultTab,
        navigate,
        routingMode,
        searchParamKey,
        searchParams,
        setSearchParams,
        isSearchParamsActive
    ]);
    return (_jsxs(Tabs, { variant: variant, index: enableRouting ? currentTabIndex : undefined, onChange: enableRouting ? handleTabChange : undefined, ...tabsProps, children: [_jsx(TabList, { w: "full", children: tabs.map(tab => (_jsx(Tab, { noOfLines: 1, w: "full", children: tab.label }, tab.slug))) }), _jsx(TabPanels, { children: tabs.map(tab => (_jsx(TabPanel, { children: _jsx(tab.component, { ...(tab.props || {}) }) }, tab.slug))) })] }));
}
